import { useState, useEffect, useMemo } from "react";
import "../styles/FilterDrawer.css";
import { TURKEY_PROVINCES } from "../../config/turkeyProvinces";
import { COUNTRY_DIAL_CODES, dialOptionLabel } from "../../config/countryDialCodes";
import api from "../../shared/AxiosConfig";
import FilterCombobox from "../components/common/FilterCombobox";
import FilterSearchInput from "../components/common/FilterSearchInput";
import { autofillOffProps, autofillOffSelectProps } from "../../shared/autofillOffProps";

export default function FilterDrawer({ open, onClose, filters, setFilters, scheme, resetPage }) {

    const [draft, setDraft] = useState(filters);
    const [termOptions, setTermOptions] = useState([]);

    const provinceOptions = useMemo(
        () => TURKEY_PROVINCES.map((p) => ({ value: p, label: p })),
        []
    );
    const dialOptions = useMemo(
        () => COUNTRY_DIAL_CODES.map((c) => ({ value: c.value, label: dialOptionLabel(c) })),
        []
    );

    useEffect(() => {
        if (open) setDraft(filters);
    }, [open, filters]);

    useEffect(() => {
        if (!open || !scheme?.some((s) => s.type === "termSelect")) {
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const res = await api.get("/terms", { params: { page: 1, limit: 200 } });
                const data = res.data?.data || [];
                if (!cancelled) setTermOptions(Array.isArray(data) ? data : []);
            } catch {
                if (!cancelled) setTermOptions([]);
            }
        })();
        return () => { cancelled = true; };
    }, [open, scheme]);

    const handleChange = (key, value) => {
        setDraft((prev) => ({ ...prev, [key]: value }));
    };

    const applyFilters = () => {
        resetPage();
        setFilters(draft);
        onClose();
    };

    const clearFilters = () => {
        resetPage();
        setFilters({});
        setDraft({});
        onClose();
    };

    return (
        <>
            <div
                className={`filter-overlay ${open ? "show" : ""}`}
                onClick={onClose}
                aria-hidden="true"
            />
            <div className={`filter-drawer ${open ? "open" : ""}`}>
                <div className="filter-header">
                    <h5>Filtrele</h5>
                    <button onClick={onClose} className="close-btn">✕</button>
                </div>

                <form
                    className="filter-body"
                    autoComplete="off"
                    onSubmit={(e) => e.preventDefault()}
                    data-lpignore="true"
                >

                    {(scheme || []).map((f) => (
                        <div key={f.key} className="mb-3">

                            <label className="filter-label" htmlFor={`filter-field-${f.key}`}>{f.label}</label>

                            {f.type === "search" && (
                                <FilterSearchInput
                                    fieldKey={f.key}
                                    drawerOpen={open}
                                    value={draft[f.key] || ""}
                                    onChange={(v) => handleChange(f.key, v)}
                                />
                            )}

                            {f.type === "select" && (
                                <select
                                    id={`filter-field-${f.key}`}
                                    className="filter-input"
                                    value={draft[f.key] || ""}
                                    onChange={(e) => handleChange(f.key, e.target.value)}
                                    {...autofillOffSelectProps}
                                >
                                    <option value="">Hepsi</option>

                                    {f.options?.map((opt, i) => {
                                        const value = typeof opt === "string" ? opt : opt.value;
                                        const label = typeof opt === "string" ? opt : opt.label;

                                        return (
                                            <option key={`${f.key}-${value}-${i}`} value={value}>
                                                {label}
                                            </option>
                                        );
                                    })}
                                </select>
                            )}

                            {f.type === "date" && (
                                <input
                                    id={`filter-field-${f.key}`}
                                    type="date"
                                    className="filter-input"
                                    value={draft[f.key] || ""}
                                    onChange={(e) => handleChange(f.key, e.target.value)}
                                    {...autofillOffProps}
                                />
                            )}

                            {f.type === "cityTurkey" && (
                                <FilterCombobox
                                    id={`filter-field-${f.key}`}
                                    value={draft[f.key] || ""}
                                    onChange={(v) => handleChange(f.key, v)}
                                    options={provinceOptions}
                                    placeholder="İl seçin veya yazın"
                                    autofillGate
                                    filterDrawerOpen={open}
                                />
                            )}

                            {f.type === "countryDial" && (
                                <FilterCombobox
                                    id={`filter-field-${f.key}`}
                                    value={draft[f.key] || ""}
                                    onChange={(v) => handleChange(f.key, v)}
                                    options={dialOptions}
                                    placeholder="+90"
                                    autofillGate
                                    filterDrawerOpen={open}
                                />
                            )}

                            {f.type === "termSelect" && (
                                <select
                                    id={`filter-field-${f.key}`}
                                    className="filter-input"
                                    value={draft[f.key] || ""}
                                    onChange={(e) => handleChange(f.key, e.target.value)}
                                    {...autofillOffSelectProps}
                                >
                                    <option value="">Hepsi</option>
                                    {termOptions.map((t) => (
                                        <option key={t.publicId} value={t.publicId}>
                                            {t.name}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                    ))}

                    <div className="filter-actions">
                        <button type="button" className="apply-btn" onClick={applyFilters}>
                            Uygula
                        </button>

                        <button type="button" className="reset-btn" onClick={clearFilters}>
                            Temizle
                        </button>
                    </div>
                </form>
            </div>
        </>
    );
}
